import {
  DetailCard,
  EditableValue,
  Tags,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useState } from "react";
import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";
import "../authorisationDetails.scss";
import {
  BASIC_INFO_FIELD_CONFIGS,
  BASIC_INFO_TITLE,
  EXTRACTED_PAYER_NAME_LABEL,
  FEEDBACK_MODE_BADGE_LABEL,
  INVALID_BADGE_LABEL,
  PATIENT_NAME_LABEL,
  REQUIRED_BASIC_INFO_FIELDS,
  SOURCE_PORTAL_LABEL,
  VALUE_CHANGED_BG,
  VALUE_CHANGED_LABEL,
  VALUE_CHANGED_TEXT,
  type ApprovalStatus,
  type BasicInfoFieldState,
} from "../utils/constants";
import { getOriginalBasicInfoState } from "../utils/saveReviewHelpers";
import RequiredAsterisk from "./RequiredAsterisk";
import SectionApprovalActions from "./SectionApprovalActions";

interface BasicInformationProps {
  jCode: JCodeDetails;
  agentDiffChangedKeys?: Set<keyof BasicInfoFieldState>;
  approvalStatus: ApprovalStatus;
  onApprovalStatusChange: (status: ApprovalStatus) => void;
  onFieldStateUpdate?: (state: BasicInfoFieldState | null) => void;
  onHasAnyChangeUpdate?: (hasAnyChange: boolean) => void;
  resetTrigger?: number;
  /**
   * When true, reject was loaded from persisted `basic_info_validated` — show Approve/Reject
   * without feedback-mode badge or field edit affordances until the user changes approval.
   */
  serverRejectHydrated?: boolean;
  /**
   * Required fields currently holding invalid values. Non-empty → show a red
   * asterisk in the card header (the section is blocking Save).
   */
  requiredInvalidFieldKeys?: Set<keyof BasicInfoFieldState>;
}

const BasicInformation = ({
  jCode,
  agentDiffChangedKeys,
  approvalStatus,
  onApprovalStatusChange,
  onFieldStateUpdate,
  onHasAnyChangeUpdate,
  resetTrigger = 0,
  serverRejectHydrated = false,
  requiredInvalidFieldKeys,
}: BasicInformationProps) => {
  const originalState = getOriginalBasicInfoState(jCode);
  const patientName = jCode.auth_letter?.patient_name || "—";
  const extractedPayerName =
    jCode.auth_letter?.extracted_payer_name || "—";
  const [fieldState, setFieldState] = useState<BasicInfoFieldState>(
    () => originalState,
  );

  useEffect(() => {
    if (resetTrigger === 0) return;
    setFieldState(getOriginalBasicInfoState(jCode));
  }, [resetTrigger, jCode]);

  const setField = useCallback(
    (key: keyof BasicInfoFieldState, value: string) => {
      setFieldState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const changedKeys = [
    ...(BASIC_INFO_FIELD_CONFIGS as { key: keyof BasicInfoFieldState }[]).filter(
      ({ key }) => fieldState[key] !== originalState[key],
    ),
    ...(fieldState.sourcePortal !== originalState.sourcePortal
      ? [{ key: "sourcePortal" as keyof BasicInfoFieldState }]
      : []),
  ].map(({ key }) => key);
  const hasAnyChange = changedKeys.length > 0;
  const showSectionValueChangedBadge = hasAnyChange;

  useEffect(() => {
    onFieldStateUpdate?.(fieldState);
    return () => {
      onFieldStateUpdate?.(null);
    };
  }, [fieldState, onFieldStateUpdate]);

  useEffect(() => {
    onHasAnyChangeUpdate?.(hasAnyChange);
  }, [hasAnyChange, onHasAnyChangeUpdate]);

  return (
    <DetailCard
      actions={
        <SectionApprovalActions
          approvalStatus={approvalStatus}
          onApprovalStatusChange={onApprovalStatusChange}
          variant="chips"
        />
      }
      badge={
        approvalStatus || (requiredInvalidFieldKeys?.size ?? 0) > 0 ? (
          <span className="flex flex-wrap items-center gap-1.5">
            {approvalStatus &&
              (showSectionValueChangedBadge ? (
                <Tags
                  label={VALUE_CHANGED_LABEL}
                  backgroundColor={VALUE_CHANGED_BG}
                  textColor={VALUE_CHANGED_TEXT}
                />
              ) : (
                <SectionApprovalActions
                  approvalStatus={approvalStatus}
                  onApprovalStatusChange={onApprovalStatusChange}
                  variant="badge"
                  invalidBadgeLabel={
                    approvalStatus === "invalid" && serverRejectHydrated
                      ? INVALID_BADGE_LABEL
                      : FEEDBACK_MODE_BADGE_LABEL
                  }
                />
              ))}
            {requiredInvalidFieldKeys &&
              requiredInvalidFieldKeys.size > 0 && <RequiredAsterisk />}
          </span>
        ) : undefined
      }
      title={BASIC_INFO_TITLE}
      headerClassName="font-semibold leading-[1.375rem] text-[0.875rem] text-primaryGray-1 [font-family:Lato]"
    >
      <div className="flex flex-col items-start gap-2.5">
        <div className="authorisation-details__row">
          {BASIC_INFO_FIELD_CONFIGS.map(({ key, label, placeholder }) => {
            const value = fieldState[key];
            const isFieldChanged =
              changedKeys.includes(key) ||
              Boolean(agentDiffChangedKeys?.has(key));
            return (
              <div key={key} className="authorisation-details__field">
                <div className="authorisation-details__label flex flex-wrap items-center gap-1.5">
                  {label}
                  {REQUIRED_BASIC_INFO_FIELDS.includes(key) && (
                    <RequiredAsterisk />
                  )}
                  {isFieldChanged && (
                    <Tags
                      label={VALUE_CHANGED_LABEL}
                      backgroundColor={VALUE_CHANGED_BG}
                      textColor={VALUE_CHANGED_TEXT}
                    />
                  )}
                </div>
                <EditableValue
                  value={value}
                  onSave={(v) => setField(key, v)}
                  placeholder={placeholder}
                  showEditButton={
                    approvalStatus === "invalid" && !serverRejectHydrated
                  }
                  className="authorisation-details__value text-[0.6875rem] leading-[1.125rem]"
                />
              </div>
            );
          })}
          <div className="authorisation-details__field">
            <div className="authorisation-details__label flex flex-wrap items-center gap-1.5">
              {PATIENT_NAME_LABEL}
            </div>
            <span className="authorisation-details__value text-[0.6875rem] leading-[1.125rem]">
              {patientName}
            </span>
          </div>
          <div className="authorisation-details__field">
            <div className="authorisation-details__label flex flex-wrap items-center gap-1.5">
              {EXTRACTED_PAYER_NAME_LABEL}
            </div>
            <span className="authorisation-details__value text-[0.6875rem] leading-[1.125rem]">
              {extractedPayerName}
            </span>
          </div>
          <div className="authorisation-details__field">
            <div className="authorisation-details__label flex flex-wrap items-center gap-1.5">
              {SOURCE_PORTAL_LABEL}
              <RequiredAsterisk />
            </div>
            <EditableValue
              value={fieldState.sourcePortal}
              onSave={(v) => setField("sourcePortal", v)}
              placeholder="Source Portal"
              showEditButton={
                approvalStatus === "invalid" && !serverRejectHydrated
              }
              className="authorisation-details__value text-[0.6875rem] leading-[1.125rem]"
            />
          </div>
        </div>
      </div>
    </DetailCard>
  );
};

export default BasicInformation;
